import { useState } from 'react';
import { NavLink, Outlet, useLocation, Navigate } from 'react-router-dom';
import { AppHeader } from '@/components/layout/AppHeader';
import { UserCircle, Building2, Tag, Users, Bell, CreditCard } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useMyClinicRole, ClinicRole } from '@/hooks/useMyClinicRole';
import { Loader2 } from 'lucide-react';
import LegalModal from '@/components/LegalModal';

const navItems = [
  {
    label: 'Profile',
    path: '/settings/my-profile',
    icon: UserCircle,
    description: 'Personal information and password',
    requiredRoles: ['owner', 'admin', 'staff'] as ClinicRole[],
  },
  {
    label: 'Clinic Profile',
    path: '/settings/profile',
    icon: Building2,
    description: 'Clinic name, type, and contact info',
    requiredRoles: ['owner', 'admin'] as ClinicRole[],
  },
  {
    label: 'Categories',
    path: '/settings/categories',
    icon: Tag,
    description: 'Policy category management',
    requiredRoles: ['owner', 'admin'] as ClinicRole[],
  },
  {
    label: 'Team Management',
    path: '/settings/team',
    icon: Users,
    description: 'Members, roles, and invitations',
    requiredRoles: ['owner', 'admin'] as ClinicRole[],
  },
  {
    label: 'Notifications',
    path: '/settings/notifications',
    icon: Bell,
    description: 'Email alerts and reminders',
    requiredRoles: ['owner', 'admin', 'staff'] as ClinicRole[],
  },
  {
    label: 'Billing',
    path: '/settings/billing',
    icon: CreditCard,
    description: 'Subscription and payment',
    requiredRoles: ['owner'] as ClinicRole[],
  },
];

export default function Settings() {
  const location = useLocation();
  const { role, isLoading } = useMyClinicRole();
  const [tosOpen, setTosOpen] = useState(false);
  const [privacyOpen, setPrivacyOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background pb-14 md:pb-0">
        <AppHeader />
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  const visibleItems = navItems.filter(
    (item) => role && item.requiredRoles.includes(role)
  );

  const firstVisiblePath = visibleItems[0]?.path ?? '/settings/my-profile';

  // Redirect /settings to first visible tab
  if (location.pathname === '/settings') {
    return <Navigate to={firstVisiblePath} replace />;
  }

  // Guard: if current path isn't in visible items, redirect
  const isPathVisible = visibleItems.some((item) => location.pathname === item.path);
  if (!isPathVisible) {
    return <Navigate to={firstVisiblePath} replace />;
  }

  return (
    <div className="min-h-screen bg-background pb-14 md:pb-0">
      <AppHeader />

      <div className="container px-4 py-8">
        {/* Page Header */}
        <div className="mb-6 md:mb-8">
          <h1 className="text-2xl md:text-[32px] font-extrabold tracking-tight text-foreground">
            Settings
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage your clinic configuration and preferences
          </p>
        </div>

        {/* Mobile: Horizontal scrolling tabs */}
        <div className="md:hidden mb-6">
          <nav className="flex gap-1 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide">
            {visibleItems.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-2 px-3 py-2 rounded-lg text-sm whitespace-nowrap transition-colors shrink-0',
                      isActive
                        ? 'bg-[hsl(var(--header-to))] text-white font-medium'
                        : 'bg-card text-muted-foreground border border-border'
                    )
                  }
                >
                  <Icon className="h-4 w-4" />
                  {item.label}
                </NavLink>
              );
            })}
          </nav>
        </div>

        {/* Layout: Sidebar (desktop) + Content */}
        <div className="md:flex md:gap-8">
          {/* Sidebar Nav - desktop only */}
          <div className="hidden md:block w-56 flex-shrink-0">
            <nav className="space-y-1">
              {visibleItems.map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    className={({ isActive }) =>
                      cn(
                        'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors',
                        isActive
                          ? 'bg-[hsl(var(--header-to))] text-white font-medium'
                          : 'text-muted-foreground hover:bg-card hover:text-foreground'
                      )
                    }
                  >
                    <Icon className="h-4 w-4" />
                    {item.label}
                  </NavLink>
                );
              })}
            </nav>
          </div>

          {/* Content Area */}
          <div className="flex-1 min-w-0">
            <div className="max-w-2xl">
              <Outlet />
            </div>
          </div>
        </div>

        {/* Legal Links Footer */}
        <div className="mt-12 pb-4 text-center">
          <button
            onClick={() => setTosOpen(true)}
            className="text-xs text-muted-foreground hover:underline"
          >
            Terms of Service
          </button>
          <span className="text-xs text-muted-foreground mx-2">·</span>
          <button
            onClick={() => setPrivacyOpen(true)}
            className="text-xs text-muted-foreground hover:underline"
          >
            Privacy Policy
          </button>
        </div>
      </div>

      <LegalModal open={tosOpen} onClose={() => setTosOpen(false)} title="Terms of Service">
        <TermsOfServiceContent />
      </LegalModal>
      <LegalModal open={privacyOpen} onClose={() => setPrivacyOpen(false)} title="Privacy Policy">
        <PrivacyPolicyContent />
      </LegalModal>
    </div>
  );
}

function TermsOfServiceContent() {
  return (
    <div className="space-y-4">
      <p className="font-semibold text-gray-900">Veracord™ Terms of Service</p>
      <p className="text-xs text-gray-500">Effective Date: March 2, 2026</p>
      <p>Welcome to Veracord™ — The System of Record. These Terms of Service govern access to and use of the Veracord platform operated by LD7 Media LLC ("Veracord," "we," "our," or "us"). By creating an account or using the Service, you agree to these Terms. If you do not agree, do not use the Service.</p>
      <h3 className="font-semibold text-gray-900">1. The Service</h3>
      <p>Veracord is a policy compliance and audit-readiness system built for healthcare clinics. The Service provides: policy storage and version control, immutable locking of approved policies, staff acknowledgment tracking, review scheduling, audit-ready export generation, and optional AI-assisted policy drafting referencing uploaded documents. Veracord is a System of Record. It is not a legal advisor, compliance certification authority, regulatory body, EMR, training platform, or risk scoring system. Final responsibility for regulatory compliance remains with the clinic.</p>
      <h3 className="font-semibold text-gray-900">2. Account Responsibility</h3>
      <p>You are responsible for maintaining accurate account information, protecting login credentials, assigning appropriate internal user permissions, and ensuring authorized use within your organization. You are responsible for all activity under your account.</p>
      <h3 className="font-semibold text-gray-900">3. Data Ownership</h3>
      <p>You retain ownership of all policies, documents, and materials uploaded to Veracord. Veracord does not claim ownership of your content. You grant Veracord a limited license to store, process, render, secure, and back up your data solely for the purpose of operating the Service.</p>
      <h3 className="font-semibold text-gray-900">4. Data Integrity & Immutability</h3>
      <p>Once a policy is approved and locked within Veracord, it becomes immutable, cannot be retroactively edited, and version history is preserved permanently. You acknowledge that locked records cannot be altered or deleted except in accordance with system design and applicable law.</p>
      <h3 className="font-semibold text-gray-900">5. Policy Builder</h3>
      <p>Policy Builder is an optional drafting assistant, powered by Vera. Its My Sources mode references only documents uploaded by the clinic and does not access public web data. Its Research mode may search the web for regulatory guidance, clearly labeled as such. In both modes, Vera does not make compliance decisions and does not replace human review. You remain fully responsible for reviewing and approving all drafted content.</p>
      <h3 className="font-semibold text-gray-900">6. Prohibited Use</h3>
      <p>You may not use the Service to store unlawful content, store patient medical records (unless explicitly supported in writing), violate healthcare privacy regulations, reverse engineer the platform, or circumvent system safeguards.</p>
      <h3 className="font-semibold text-gray-900">7. Subscription & Payment</h3>
      <p>Veracord operates on a per-clinic monthly subscription. Fees are billed in advance. Failure to pay may result in account suspension. Veracord reserves the right to update pricing with advance notice.</p>
      <h3 className="font-semibold text-gray-900">8. Security</h3>
      <p>Veracord implements commercially reasonable security safeguards including encrypted data storage, role-based access controls, and secure hosting infrastructure. No system is guaranteed to be completely secure. You are responsible for maintaining appropriate internal security practices.</p>
      <h3 className="font-semibold text-gray-900">9. Availability</h3>
      <p>We strive for high availability but do not guarantee uninterrupted access. Scheduled maintenance or unforeseen outages may occur.</p>
      <h3 className="font-semibold text-gray-900">10. Termination</h3>
      <p>You may terminate your subscription at any time. Upon termination, access to the Service will end and data retention policies will apply as stated in our Privacy Policy.</p>
      <h3 className="font-semibold text-gray-900">11. Limitation of Liability</h3>
      <p>To the maximum extent permitted by law, Veracord shall not be liable for regulatory penalties, audit failures, business losses, or indirect or consequential damages. Veracord provides documentation tools. Compliance outcomes depend on proper use by the clinic.</p>
      <h3 className="font-semibold text-gray-900">12. Governing Law</h3>
      <p>These Terms are governed by the laws of the State of Florida, without regard to conflict of law principles.</p>
      <h3 className="font-semibold text-gray-900">13. Updates</h3>
      <p>We may update these Terms from time to time. Continued use of the Service constitutes acceptance of the revised Terms.</p>
    </div>
  );
}

function PrivacyPolicyContent() {
  return (
    <div className="space-y-4">
      <p className="font-semibold text-gray-900">Veracord™ Privacy Policy</p>
      <p className="text-xs text-gray-500">Effective Date: March 2, 2026</p>
      <p>Veracord respects your privacy. This Privacy Policy explains how we collect, use, and protect information when you use the Veracord platform.</p>
      <h3 className="font-semibold text-gray-900">1. Information We Collect</h3>
      <p>Account information (name, email, clinic name, role). Usage information (login activity, acknowledgment timestamps, system interaction logs). Uploaded content (policies, regulatory documents, internal compliance documentation). Veracord does not collect patient medical records unless explicitly supported and contracted.</p>
      <h3 className="font-semibold text-gray-900">2. How We Use Information</h3>
      <p>To provide and operate the Service, generate audit reports, maintain immutable records, secure the platform, and improve product performance. We do not sell your data. We do not use your data for advertising. We do not use clinic policy content to train public AI models.</p>
      <h3 className="font-semibold text-gray-900">3. Data Security</h3>
      <p>We implement encryption at rest and in transit, role-based access controls, secure cloud hosting, and audit logging. Access is restricted to authorized personnel.</p>
      <h3 className="font-semibold text-gray-900">4. Data Retention</h3>
      <p>We retain data while your account is active, as required for compliance record integrity, and as required by law. You may request export of your data prior to termination.</p>
      <h3 className="font-semibold text-gray-900">5. Data Sharing</h3>
      <p>We do not share clinic data with third parties except payment processors, secure infrastructure providers, and legal requirements (court order, subpoena). We do not sell or monetize clinic data.</p>
      <h3 className="font-semibold text-gray-900">6. AI Data Handling</h3>
      <p>Documents uploaded for AI drafting are processed within Veracord's controlled environment, are not used to train external AI systems, and remain confidential.</p>
      <h3 className="font-semibold text-gray-900">7. Your Rights</h3>
      <p>You may access your account data, update account information, request data export, and request account deletion (subject to legal record requirements).</p>
      <h3 className="font-semibold text-gray-900">8. Children</h3>
      <p>Veracord is not intended for individuals under 18.</p>
      <h3 className="font-semibold text-gray-900">9. Updates</h3>
      <p>We may update this Privacy Policy periodically. Continued use of the Service constitutes acceptance of changes.</p>
    </div>
  );
}
